import { useEffect, useState } from 'react';

export interface VideoMeasures {
  /** Pixels. Null while the file loads, and when the browser cannot read it. */
  width: number | null;
  /** Pixels. Null while the file loads, and when the browser cannot read it. */
  height: number | null;
  /** Null while the file loads, and when the browser cannot read it. */
  durationSeconds: number | null;
  /** True until the browser gave the measures or failed. */
  isLoading: boolean;
}

type Measures = Omit<VideoMeasures, 'isLoading'>;

const NO_MEASURES: Measures = { width: null, height: null, durationSeconds: null };

/** How long the hook waits for the browser. A file that fires no event has no measures after it. */
export const VIDEO_MEASURE_TIMEOUT_MS = 10_000;

function positiveOrNull(value: number): number | null {
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Measures a video in the browser. The server stores the measures of a file only at its first
 * publish, so the Composer cannot ask the server before that. The hook loads only the metadata
 * in a `<video>` element that is not in the page. It gives nulls while the file loads and when
 * the load fails, so it never blocks scheduling: a file with no measures goes out as a normal
 * video (the safe path of `resolvePostFormat`). A browser can fire neither `loadedmetadata` nor
 * `error` (a stalled download, a codec it does not read), so the hook stops the wait after 10
 * seconds and gives no measures.
 */
export function useVideoMeasures(source: File | string | null | undefined): VideoMeasures {
  const [result, setResult] = useState<{ source: File | string; measures: Measures }>();

  useEffect(() => {
    if (source === null || source === undefined) {
      return undefined;
    }
    const video = document.createElement('video');
    const url = typeof source === 'string' ? source : URL.createObjectURL(source);

    // The first of the three ends the wait: the metadata, an error, or the timeout.
    const timeout = window.setTimeout(() => setResult({ source, measures: NO_MEASURES }), VIDEO_MEASURE_TIMEOUT_MS);

    const onMeasured = () => {
      window.clearTimeout(timeout);
      setResult({
        source,
        measures: {
          width: positiveOrNull(video.videoWidth),
          height: positiveOrNull(video.videoHeight),
          durationSeconds: positiveOrNull(video.duration),
        },
      });
    };
    const onFailed = () => {
      window.clearTimeout(timeout);
      setResult({ source, measures: NO_MEASURES });
    };

    video.preload = 'metadata';
    video.muted = true;
    video.addEventListener('loadedmetadata', onMeasured);
    video.addEventListener('error', onFailed);
    video.src = url;

    return () => {
      window.clearTimeout(timeout);
      video.removeEventListener('loadedmetadata', onMeasured);
      video.removeEventListener('error', onFailed);
      // Stops the download of a file that is not needed any more.
      video.removeAttribute('src');
      if (typeof source !== 'string') {
        URL.revokeObjectURL(url);
      }
    };
  }, [source]);

  if (source === null || source === undefined) {
    return { ...NO_MEASURES, isLoading: false };
  }
  // The measures of the file before this one are not the measures of this file.
  if (result === undefined || result.source !== source) {
    return { ...NO_MEASURES, isLoading: true };
  }
  return { ...result.measures, isLoading: false };
}
